/**
 * KAN-952: "С чего начать" button on every cabinet page (not on /m/*).
 * Opens the tutorial modal with the onboarding video (dashboard section list
 * next to it). The cross hides the button for good in this browser and moves
 * focus to the page tutorial button (or to the content when there is none).
 *
 * Placement: standalone (fixed) above a bottom-right page button or alone at
 * the bottom right; on top-right pages (chat) the page button renders it
 * `inline` in its own row, so it never goes below the top bar.
 */
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Rocket, X } from 'lucide-react';
import { isMobileAppPath } from '@/lib/mobileApp';
import { cn } from '@/lib/utils';
import { buildVideoUrl, ONBOARDING_TUTORIAL_SLUG, TUTORIALS } from '@/tutorials/registry';
import { useCurrentTutorial } from './useCurrentTutorial';
import { useOnboardingDismissed } from './useOnboardingDismissed';
import { onboardingPositionClass } from './onboardingPlacement';
import { TutorialDialog } from './TutorialDialog';

const FALLBACK_TITLE = 'С чего начать';

/** Focus target after the cross: the page tutorial button, else the cabinet content. */
function moveFocusAfterDismiss(): void {
  const target =
    document.querySelector<HTMLElement>('[data-tutorial-open]') ??
    document.querySelector<HTMLElement>('main');
  target?.focus();
}

interface OnboardingButtonProps {
  /** Rendered inside the top-right page tutorial row instead of its own fixed spot. */
  inline?: boolean;
}

export function OnboardingButton({ inline = false }: OnboardingButtonProps) {
  const { pathname } = useLocation();
  const tutorial = useCurrentTutorial();
  const [dismissed, dismiss] = useOnboardingDismissed();
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(0);

  if (dismissed || isMobileAppPath(pathname)) return null;
  // Top-right pages: the page tutorial button renders the inline copy.
  if (!inline && tutorial?.placement === 'top-right') return null;

  const entry = TUTORIALS.find((item) => item.slug === ONBOARDING_TUTORIAL_SLUG);
  const start = {
    slug: ONBOARDING_TUTORIAL_SLUG,
    title: entry?.title ?? FALLBACK_TITLE,
    videoUrl: buildVideoUrl(ONBOARDING_TUTORIAL_SLUG),
  };
  const compact = inline;

  const handleDismiss = () => {
    moveFocusAfterDismiss();
    dismiss();
  };

  const openModal = () => {
    setSession((value) => value + 1);
    setOpen(true);
  };

  return (
    <>
      <div
        data-testid="onboarding-button-root"
        data-inline={String(inline)}
        className={cn(
          'flex items-center gap-1 print:hidden',
          inline ? 'mr-2' : cn('fixed z-40', onboardingPositionClass(tutorial?.placement))
        )}
      >
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Скрыть кнопку С чего начать"
          title="Скрыть навсегда"
          className="flex h-7 w-7 items-center justify-center rounded-full bg-white text-gray-500 shadow ring-1 ring-gray-200 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
        <span className="relative inline-flex">
          <span
            aria-hidden="true"
            data-testid="onboarding-button-pulse"
            className="pointer-events-none absolute inset-0 rounded-full bg-amber-400 opacity-60 motion-safe:animate-ping"
            style={{ animationIterationCount: 5, animationFillMode: 'forwards' }}
          />
          <button
            type="button"
            onClick={openModal}
            aria-label="С чего начать"
            title={start.title}
            className={cn(
              'relative inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 font-semibold text-white shadow-lg shadow-amber-500/40 ring-4 ring-amber-200 transition hover:scale-105 hover:bg-amber-600 focus:outline-none focus-visible:ring-amber-400',
              compact ? 'h-10 px-4 text-sm' : 'h-12 px-5 text-base'
            )}
          >
            <Rocket className={compact ? 'h-5 w-5' : 'h-6 w-6'} aria-hidden="true" />
            <span>С чего начать</span>
          </button>
        </span>
      </div>
      <TutorialDialog
        key={session}
        open={open}
        onOpenChange={setOpen}
        start={start}
      />
    </>
  );
}
